import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowRight, Copy, MessageSquare, Pause, Play, Plus } from 'lucide-react';
import { api, type AccountRow, type KommoPipeline, type PipelineRow, type SheetRow } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const ALL_EVENTS = ['add_lead', 'update_lead', 'status_lead'] as const;

interface CreatePayload {
  kommo_account_id: string;
  sheet_target_id: string;
  pipeline_ids: number[];
  status: 'active' | 'paused';
  trigger_events: string[];
  field_mapping: never[];
}

export function Pipelines(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState<CreatePayload>({
    kommo_account_id: '',
    sheet_target_id: '',
    pipeline_ids: [],
    status: 'paused',
    trigger_events: ['add_lead', 'update_lead', 'status_lead'],
    field_mapping: [],
  });

  // Список воронок выбранного Kommo-аккаунта для multi-select
  const { data: kommoPipelines = [] } = useQuery({
    queryKey: ['kommo-pipelines', form.kommo_account_id],
    queryFn: () =>
      api.get<KommoPipeline[]>(`/admin/accounts/${form.kommo_account_id}/kommo-pipelines`),
    enabled: Boolean(form.kommo_account_id),
  });

  const togglePipelineId = (id: number): void => {
    setForm((prev) => ({
      ...prev,
      pipeline_ids: prev.pipeline_ids.includes(id)
        ? prev.pipeline_ids.filter((x) => x !== id)
        : [...prev.pipeline_ids, id],
    }));
  };

  const { data: pipelines = [], isLoading } = useQuery({
    queryKey: ['pipelines'],
    queryFn: () => api.get<PipelineRow[]>('/admin/pipelines'),
  });

  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<AccountRow[]>('/admin/accounts'),
  });

  const { data: sheets = [] } = useQuery({
    queryKey: ['sheets'],
    queryFn: () => api.get<SheetRow[]>('/admin/sheets'),
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreatePayload) => api.post<{ id: string }>('/admin/pipelines', payload),
    onSuccess: ({ id }) => {
      queryClient.invalidateQueries({ queryKey: ['pipelines'] });
      setIsOpen(false);
      navigate(`/pipelines/${id}`);
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'paused' }) =>
      api.put(`/admin/pipelines/${id}`, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pipelines'] }),
  });

  const toggleTriggerEvent = (event: string): void => {
    setForm((prev) => ({
      ...prev,
      trigger_events: prev.trigger_events.includes(event)
        ? prev.trigger_events.filter((e) => e !== event)
        : [...prev.trigger_events, event],
    }));
  };

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Pipelines</h1>
          <p className="text-sm text-muted-foreground">Связки Kommo CRM ↔ Google Sheets</p>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button disabled={accounts.length === 0 || sheets.length === 0}>
              <Plus className="h-4 w-4" />
              Создать pipeline
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                createMutation.mutate(form);
              }}
              className="space-y-4"
            >
              <DialogHeader>
                <DialogTitle>Новая связка</DialogTitle>
                <DialogDescription>
                  После создания вы попадёте на страницу маппинга полей.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-2">
                <Label htmlFor="kommo">Kommo аккаунт</Label>
                <select
                  id="kommo"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={form.kommo_account_id}
                  onChange={(e) => setForm({ ...form, kommo_account_id: e.target.value })}
                  required
                >
                  <option value="">— выбрать —</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.display_name} ({a.subdomain})
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="sheet">Google Sheet</Label>
                <select
                  id="sheet"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={form.sheet_target_id}
                  onChange={(e) => setForm({ ...form, sheet_target_id: e.target.value })}
                  required
                >
                  <option value="">— выбрать —</option>
                  {sheets.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.display_name} / {s.worksheet_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label>Воронки Kommo</Label>
                {form.kommo_account_id ? (
                  kommoPipelines.length === 0 ? (
                    <div className="text-xs text-muted-foreground">Загрузка списка воронок…</div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {kommoPipelines.map((p) => {
                        const checked = form.pipeline_ids.includes(p.id);
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => togglePipelineId(p.id)}
                            className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                              checked
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-border text-muted-foreground hover:bg-accent'
                            }`}
                          >
                            {p.name}
                            {p.is_main ? ' ★' : ''}
                          </button>
                        );
                      })}
                      <p className="w-full text-xs text-muted-foreground">
                        {form.pipeline_ids.length === 0
                          ? 'Не выбрано ни одной — будут синхронизироваться все воронки'
                          : `Выбрано: ${form.pipeline_ids.length}`}
                      </p>
                    </div>
                  )
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Сначала выберите Kommo-аккаунт
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Триггеры (типы webhook-событий)</Label>
                <div className="flex flex-wrap gap-2">
                  {ALL_EVENTS.map((event) => {
                    const checked = form.trigger_events.includes(event);
                    return (
                      <button
                        key={event}
                        type="button"
                        onClick={() => toggleTriggerEvent(event)}
                        className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                          checked
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border text-muted-foreground hover:bg-accent'
                        }`}
                      >
                        {event}
                      </button>
                    );
                  })}
                </div>
              </div>

              {createMutation.error && (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
                  {(createMutation.error as Error).message}
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                  Отмена
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Создаём…' : 'Создать'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      {(accounts.length === 0 || sheets.length === 0) && (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            Сначала добавьте хотя бы один <Link to="/accounts" className="text-primary underline">Kommo-аккаунт</Link> и одну{' '}
            <Link to="/sheets" className="text-primary underline">Google-таблицу</Link>.
          </CardContent>
        </Card>
      )}

      <LastNoteBanner />
      <AiSummaryBanner />

      {isLoading && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {[1, 2].map((i) => (
            <Card key={i} className="h-64 animate-pulse">
              <CardHeader>
                <div className="h-5 w-3/4 rounded bg-muted" />
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="h-4 rounded bg-muted" />
                <div className="h-4 rounded bg-muted" />
                <div className="h-4 rounded bg-muted" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!isLoading && pipelines.length === 0 && accounts.length > 0 && sheets.length > 0 && (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            Pipelines пока нет. Нажмите «Создать pipeline».
          </CardContent>
        </Card>
      )}

      <motion.div
        className="grid grid-cols-1 gap-4 md:grid-cols-2"
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
      >
        {pipelines.map((p) => (
          <motion.div
            key={p.id}
            variants={{
              hidden: { opacity: 0, y: 12 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } },
            }}
          >
          <Card>
            <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
              <CardTitle className="text-base">{p.display_name}</CardTitle>
              <Badge variant={p.status === 'active' ? 'success' : 'secondary'}>
                {p.status === 'active' ? 'Active' : 'Paused'}
              </Badge>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row label="Kommo">
                <span className="text-muted-foreground">{p.kommo_account.subdomain}</span>
              </Row>
              <Row label="Sheet">
                <span className="text-muted-foreground">
                  {p.sheet_target.display_name} / {p.sheet_target.worksheet_name}
                </span>
              </Row>
              <Row label="Воронки">
                <span className="text-muted-foreground">
                  {p.pipeline_ids.length === 0 ? 'все' : `${p.pipeline_ids.length} выбрано`}
                </span>
              </Row>
              <Row label="Триггеры">
                <span className="text-muted-foreground">
                  {p.trigger_events.length === 0 ? '—' : p.trigger_events.join(', ')}
                </span>
              </Row>
              <Row label="Поля">
                <span className="text-muted-foreground">
                  {p.field_mapping?.length ?? 0} замапленных
                </span>
              </Row>
              <div className="mt-3 flex items-center gap-2">
                <code className="flex-1 truncate rounded bg-muted px-2 py-1 text-xs">{p.webhook_url}</code>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => void navigator.clipboard.writeText(p.webhook_url)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between border-t pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  toggleMutation.mutate({
                    id: p.id,
                    status: p.status === 'active' ? 'paused' : 'active',
                  })
                }
                disabled={toggleMutation.isPending}
              >
                {p.status === 'active' ? (
                  <>
                    <Pause className="h-3.5 w-3.5" /> Pause
                  </>
                ) : (
                  <>
                    <Play className="h-3.5 w-3.5" /> Resume
                  </>
                )}
              </Button>
              <Button asChild size="sm">
                <Link to={`/pipelines/${p.id}`}>
                  Открыть детали
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </Button>
            </CardFooter>
          </Card>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-right text-sm">{children}</span>
    </div>
  );
}

function LastNoteBanner(): JSX.Element | null {
  const { data } = useQuery({
    queryKey: ['last-note-overview-summary'],
    queryFn: () =>
      api.get<{
        enabled: boolean;
        stats: Record<string, { '24h': number; '7d': number }>;
      }>('/admin/last-note/overview'),
    refetchInterval: 30_000,
  });
  if (!data) return null;
  const done = data.stats.done?.['24h'] ?? 0;
  const skipped = data.stats.skipped?.['24h'] ?? 0;
  const failed = (data.stats.failed?.['24h'] ?? 0) + (data.stats.dead?.['24h'] ?? 0);
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
        <div className="flex items-center gap-3">
          <MessageSquare className="h-5 w-5 text-primary" />
          <div>
            <div className="text-sm font-semibold">Last Note</div>
            <div className="text-xs text-muted-foreground">
              {data.enabled
                ? `Активен. За 24 часа: ${done} записано, ${skipped} пропущено${failed > 0 ? `, ${failed} ошибок` : ''}.`
                : 'Не сконфигурирован — env-переменные KOMMO_LAST_NOTE_* пустые.'}
            </div>
          </div>
        </div>
        <Button asChild size="sm" variant="outline">
          <Link to="/last-note">Открыть</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function AiSummaryBanner(): JSX.Element {
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-sm bg-primary/10 text-xs">✦</span>
          <div>
            <div className="text-sm font-semibold">AI Weekly Summary</div>
            <div className="text-xs text-muted-foreground">
              Раз в неделю Claude пишет в Kommo сводку по сделкам выбранных воронок.
            </div>
          </div>
        </div>
        <Button asChild size="sm" variant="outline">
          <Link to="/ai-summary">Открыть</Link>
        </Button>
      </CardContent>
    </Card>
  );
}
